import { useCallback } from "react"
import useEmblaCarousel from "embla-carousel-react"
import { motion } from "framer-motion"
import { ChevronLeft, ChevronRight, PackageSearch } from "lucide-react"
import { useProducts } from "@/hooks/useProducts"
import { useCart } from "@/hooks/useCart"
import { useLanguage } from "@/hooks/useLanguage"
import { ProductCardItem } from "@/components/catalog/ProductCardItem"
import { ProductResponseDto } from "@/api/api-client"
import { toast } from "@/lib/toast"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"

export const FeaturedProducts = () => {
    const { t } = useLanguage()
    const text = t.featuredProducts
    const { addItem } = useCart()
    const navigate = useNavigate()
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: "start",
        loop: false,
        dragFree: true,
    })

    // Fetch some products - assuming no specific filters for "top items", just grabbing a few.
    const { data: productPage, isLoading } = useProducts({ pageSize: 12, sortBy: "newest" })
    const products = productPage?.items ?? []

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

    const content = t.productCatalog

    const handleAddToCart = (product: ProductResponseDto) => {
        addItem(product as any)
        toast.success(`${product.name} ${content.addToCart}`)
    }

    const handleContactForProduct = (productName: string) => {
        toast.info(`${content.contact}: ${productName}`)
    }

    if (isLoading) {
        return (
            <section className="py-24">
                <div className="container mx-auto flex h-64 items-center justify-center px-4">
                    <div className="h-12 w-12 animate-spin border-4 border-primary border-t-transparent" />
                </div>
            </section>
        )
    }

    if (products.length === 0) {
        return null
    }

    return (
        <section className="relative overflow-hidden bg-muted/20 py-16">
            {/* Background Aesthetic */}
            <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-muted to-transparent opacity-50" />

            <div className="container relative z-10 mx-auto px-4">
                <div className="mb-12 flex items-end justify-between">
                    <div className="max-w-2xl">
                        <motion.h2
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="font-display text-4xl tracking-widest text-foreground md:text-5xl"
                        >
                            {text.title}
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="mt-2 text-lg text-muted-foreground"
                        >
                            {text.subtitle}
                        </motion.p>
                    </div>

                    <div className="hidden items-center gap-4 md:flex">
                        <Button
                            variant="outline"
                            className="mr-6 rounded-none font-display uppercase tracking-widest transition-all hover:bg-primary hover:text-white"
                            onClick={() => navigate("/catalog/marketplace")}
                        >
                            {text.viewAll}
                        </Button>
                        <button
                            onClick={scrollPrev}
                            className="group flex h-12 w-12 items-center justify-center border-2 border-foreground/10 bg-background transition-all hover:border-primary hover:bg-primary"
                        >
                            <ChevronLeft className="h-5 w-5 text-foreground/50 transition-colors group-hover:text-primary-foreground" />
                        </button>
                        <button
                            onClick={scrollNext}
                            className="group flex h-12 w-12 items-center justify-center border-2 border-foreground/10 bg-background transition-all hover:border-primary hover:bg-primary"
                        >
                            <ChevronRight className="h-5 w-5 text-foreground/50 transition-colors group-hover:text-primary-foreground" />
                        </button>
                    </div>
                </div>

                <div className="overflow-hidden" ref={emblaRef}>
                    <div className="-ml-2 flex touch-pan-y px-2 py-4">
                        {products.map((product, index) => (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                key={product.id}
                                className="min-w-[75vw] flex-[0_0_75vw] pl-6 sm:min-w-[40vw] sm:flex-[0_0_40vw] md:min-w-[30vw] md:flex-[0_0_30vw] lg:min-w-[20vw] lg:flex-[0_0_20vw]"
                            >
                                <div className="h-full">
                                    <ProductCardItem
                                        product={product}
                                        onAddToCart={handleAddToCart}
                                        onContact={handleContactForProduct}
                                        viewMode="grid"
                                        text={{
                                            price: content.price,
                                            contact: content.contact,
                                            addToCart: content.addToCart,
                                            incVat: content.incVat,
                                        }}
                                    />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}

